import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import { Paste } from 'components/Icons';
import { get } from 'lodash';

import { Button } from 'components/Button';
import { FormRow, Input } from 'components/Form';
import Validator from 'components/Validator';
import GeneralError from 'components/GeneralError';
import Alert from 'components/Alert';
import InfoTooltip from 'components/InfoTooltip';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction } from 'actions/workflow';

import schema from './schema';

const CollateralReturn = ({
  navigate,
  searchParams,
  dispatch,
  validator: { onChangeHandler, values, errors, validate }
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const collateralValue = get(checkinData, 'quantity');
  const collateralCoin = (get(checkinData, 'coin') || '').toUpperCase();
  const [generalError, setGeneralError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasted, setIsPasted] = useState(false);

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validate().isValid) return;

    const { customerAddress } = values;

    setIsSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: { customerAddress },
      success: (res) => {
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
        setIsSubmitting(false);
      },
      fail: handleFail,
    }));
  };

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const pasteClipboardToWallet = async () => {
    try {
      const text = await navigator.clipboard.readText(values.customerAddress);
      onChangeHandler('customerAddress', text);
      setIsPasted(true);
      setTimeout(() => setIsPasted(false), 2000);
    } catch (err) {
      console.error('Failed to paste text: ', err);
    }
  };

  return (
    <div>
      <div className="flex items-stretch gap-4 justify-between px-4 py-6 bg-blue-100 rounded mb-6">
        <div className="flex flex-col gap-8">
          <h2 className="font-semibold mb-1">Collateral Amount</h2>
          <p className="text-sm">Released after your wallet is verified, minus a small network (gas) fee.</p>
        </div>
        <div className="flex items-center justify-center text-blue-500 font-bold text-2xl w-[30%] border border-l-slate-300 text-center">
          {collateralValue} {collateralCoin}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="font-semibold mb-1">BTC Wallet Address</h2>
        <p className="text-sm text-slate-800 mb-4">We will send a micro-deposit to this wallet. You will need to confirm the amount to proceed.</p>

        <div className="relative">
          <Input
            placeholder="Enter your wallet address"
            value={values.customerAddress}
            error={errors.customerAddress}
            onChange={(e) => handleChange('customerAddress', e.target.value)}
            required
          />
          <div className="flex items-center gap-2 absolute top-3.5 right-3">
            <InfoTooltip tooltip={isPasted ? '' : 'Paste'} position="top" className="p-1 rounded-md bg-white border border-slate-200">
              <span
                className="relative flex items-center justify-center w-[14px] h-[14px]"
                onClick={pasteClipboardToWallet}
              >
                <Paste size={14} className={`absolute transition-opacity duration-300 ${isPasted ? 'opacity-0' : 'opacity-100'}`} />
                <Check size={14} className={`absolute transition-opacity duration-300 text-green-500 ${isPasted ? 'opacity-100' : 'opacity-0'}`} />
              </span>
            </InfoTooltip>
          </div>
        </div>
      </div>

      <Alert
        className="mb-8"
        title="Secure Transfer Process"
      >
        <p>Your collateral is held in secure custody and returned directly to you.</p>
      </Alert>

      <GeneralError error={generalError} />

      <FormRow className="md:!flex-col !gap-2">
        <Button
          className="w-full"
          label="Confirm Address & Send Micro-Deposit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={!get(values, 'customerAddress')}
        />
        <span className="text-xs text-slate-500 px-1 text-center leading-[1.6]">
          Micro-deposits can take up to 24 hours to appear depending on network conditions.
          <br />
          Questions? Call us at
          {' '}
          <a href="tel:+18447216961" className="whitespace-nowrap text-blue-500 font-semibold">
            (844) 721-6961
          </a>
          .
        </span>
      </FormRow>
    </div>
  );
};

CollateralReturn.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
};

export default Validator(schema)(CollateralReturn);
